import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ApiError } from '@/lib/api-client';
import { formatGermanShortDate } from '@/lib/format-german-date';
import { STRINGS } from '@/strings/strings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { CustomerCollectionOrder } from '../api';
import { discardBookOrder, listBookOrders } from '../api';
import { useAuthStore } from '@/stores/authStore';
import { apiFetch } from '@/lib/api-client';
import type { User } from '@/lib/auth-types';
import { useBookConfiguratorStore } from '../state/book-configurator-store';
import { clearAllPhotoPicks } from '../state/photo-picks-idb';
import { deriveBookSlot } from '../book-slot';

/**
 * Meine Bücher — the customer's list of book orders. Sebastian
 * ruling 2026-09-15.
 *
 * Behaviour matrix:
 *   0 orders   → empty state + "Neues Buch anlegen" CTA
 *   1..2 open  → list + "Neues Buch anlegen" CTA
 *   3 open     → list + disabled CTA + capReachedHelper line
 *   paid       → card in the list, no Verwerfen button
 *
 * "Neues Buch anlegen" RESETS the configurator localStorage +
 * clears the photo-picks IndexedDB before navigating to /book so the
 * customer starts with an empty configurator (avoids the leak where
 * a previous order's cast/photos linger in the browser after a new
 * book was intended).
 *
 * "Verwerfen" on a card fires the confirmation dialog, calls DELETE
 * /api/book/orders/:orderId, and on success re-fetches the list AND
 * /api/users/me (to refresh `pendingBookOrderId` in the auth store —
 * otherwise a subsequent login redirect could route back to the
 * deleted order).
 */

const OPEN_BOOKS_PER_USER_CAP = 3;

// PRE_PAYMENT_OPEN_STATUSES — mirror of backend `book-order-status-
// sets.ts`. Kept local; the client uses it only to (a) count against
// the cap and (b) show/hide the "Verwerfen" button.
const PRE_PAYMENT_OPEN: ReadonlySet<string> = new Set([
  'draft_configuring',
  'light_gate_pending',
  'summary_review',
  'photo_rejected',
  // 2026-10-14 Part 5 defect 3 fix — mirror of backend
  // PRE_PAYMENT_OPEN_STATUSES (updated in Part 4). Previously missing
  // here meant orders in this state had no Verwerfen button in
  // Meine Bücher while other open pre-payment drafts did. Backend
  // discard already accepts this status via PRE_PAYMENT_DISCARDABLE_
  // STATUSES = PRE_PAYMENT_OPEN_STATUSES minus checkout_pending.
  'block_derivation_rejected',
  'anchor_pending',
  'anchor_review',
  'anchor_approved',
  'story_generating',
  'text_editing',
  'preview_rendering',
  'preview_ready',
  'checkout_pending',
]);

// checkout_pending is open (counts against cap) but NOT discardable —
// mirror of backend `PRE_PAYMENT_DISCARDABLE_STATUSES`.
const DISCARDABLE: ReadonlySet<string> = new Set(
  [...PRE_PAYMENT_OPEN].filter((s) => s !== 'checkout_pending'),
);

export function MeineBuecherRoute() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const [orders, setOrders] = useState<readonly CustomerCollectionOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOrderId, setConfirmOrderId] = useState<string | null>(null);
  const [discardPending, setDiscardPending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await listBookOrders();
      setOrders(res.orders);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleNewBook = useCallback(async () => {
    useBookConfiguratorStore.getState().reset();
    // Photo-pick IDB clear is fire-and-forget by design (same
    // discipline as LightGateOverlay success path).
    void clearAllPhotoPicks();
    // Sebastian ruling 2026-09-18 — deliberate-intent marker for the
    // PersonenRoute mount redirect. The redirect at PersonenRoute
    // (route-fix 2026-08-06) sends any visit to /book back to the
    // customer's pending order; a click here must be exempt because
    // the customer explicitly wants a fresh configurator. Router
    // navigation state travels only on this history entry;
    // PersonenRoute consumes it and clears the entry so a reload or
    // a later visit to /book falls through to the redirect as before.
    navigate('/book', { state: { fromNewBookIntent: true } });
  }, [navigate]);

  const handleDiscardConfirm = useCallback(async () => {
    if (confirmOrderId === null) return;
    setDiscardPending(true);
    try {
      await discardBookOrder(confirmOrderId);
      // Refresh the list AND /me. /me refresh keeps
      // useAuthStore.user.pendingBookOrderId in sync — if the
      // discarded order was the pending one, the next login redirect
      // would otherwise route to /book/<deleted-id> and 404.
      const [, fresh] = await Promise.all([load(), apiFetch<User>('/api/users/me')]);
      setUser(fresh);
      setConfirmOrderId(null);
    } catch (err) {
      if (err instanceof ApiError && typeof err.envelope?.user_message === 'string') {
        setError(err.envelope.user_message);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setDiscardPending(false);
    }
  }, [confirmOrderId, load, setUser]);

  if (orders === null) {
    return (
      <div className="mx-auto flex w-full max-w-[680px] flex-col items-center gap-4 px-4 pt-16">
        <Loader2 aria-hidden className="h-6 w-6 animate-spin text-dream-navy/60" />
      </div>
    );
  }

  const openCount = orders.filter((o) => PRE_PAYMENT_OPEN.has(o.status)).length;
  const capReached = openCount >= OPEN_BOOKS_PER_USER_CAP;

  if (orders.length === 0) {
    return (
      <div className="mx-auto flex w-full max-w-[680px] flex-col gap-6 px-4 pt-8 pb-16">
        <h1 className="text-2xl font-semibold text-dream-navy">
          {STRINGS.book.meineBuecher.pageHeading}
        </h1>
        <div className="flex flex-col items-center gap-3 rounded-xl border border-book-border bg-book-card px-6 py-12 text-center">
          <p className="text-lg font-semibold text-dream-navy">
            {STRINGS.book.meineBuecher.emptyHeading}
          </p>
          <p className="text-sm text-book-text-secondary">
            {STRINGS.book.meineBuecher.emptyBody}
          </p>
          <button
            type="button"
            onClick={handleNewBook}
            className="mt-4 h-11 rounded-lg bg-dream-gold px-6 text-sm font-bold text-dream-navy hover:opacity-90"
          >
            {STRINGS.book.meineBuecher.newBookButton}
          </button>
        </div>
        {error !== null && (
          <p role="alert" className="text-sm text-error">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[680px] flex-col gap-4 px-4 pt-8 pb-16">
      <h1 className="text-2xl font-semibold text-dream-navy">
        {STRINGS.book.meineBuecher.pageHeading}
      </h1>
      <ul className="flex flex-col gap-3">
        {orders.map((order) => (
          <li key={order.bookOrderId}>
            <BookCard
              order={order}
              onOpen={() => navigate(`/book/${order.bookOrderId}`)}
              onDiscardClick={() => setConfirmOrderId(order.bookOrderId)}
            />
          </li>
        ))}
      </ul>
      <div className="flex flex-col items-start gap-2 pt-2">
        <button
          type="button"
          onClick={handleNewBook}
          disabled={capReached}
          className="h-11 rounded-lg bg-dream-gold px-6 text-sm font-bold text-dream-navy hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {STRINGS.book.meineBuecher.newBookButton}
        </button>
        {capReached && (
          <p className="text-sm text-book-text-secondary">
            {STRINGS.book.meineBuecher.capReachedHelper}
          </p>
        )}
      </div>
      {error !== null && (
        <p role="alert" className="text-sm text-error">
          {error}
        </p>
      )}

      <Dialog
        open={confirmOrderId !== null}
        onOpenChange={(open) => {
          if (!open && !discardPending) setConfirmOrderId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{STRINGS.book.meineBuecher.discardDialog.heading}</DialogTitle>
            <DialogDescription>
              {STRINGS.book.meineBuecher.discardDialog.body}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmOrderId(null)}
              disabled={discardPending}
              className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5 disabled:opacity-50"
            >
              {STRINGS.book.meineBuecher.discardDialog.cancel}
            </button>
            <button
              type="button"
              onClick={handleDiscardConfirm}
              disabled={discardPending}
              aria-busy={discardPending}
              className="flex h-10 items-center justify-center gap-2 rounded-lg bg-error px-4 text-sm font-bold text-dream-white hover:opacity-90 disabled:opacity-60"
            >
              {discardPending && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              {STRINGS.book.meineBuecher.discardDialog.confirm}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookCard({
  order,
  onOpen,
  onDiscardClick,
}: {
  readonly order: CustomerCollectionOrder;
  readonly onOpen: () => void;
  readonly onDiscardClick: () => void;
}) {
  const isDiscardable = DISCARDABLE.has(order.status);
  const statusLabel = getStatusLabel(order.status);
  const displayTitle = order.title.length > 0 ? order.title : statusLabel;
  const createdDate = formatGermanShortDate(order.createdAt);
  const secondaryLine =
    order.castNames.length > 0
      ? STRINGS.book.meineBuecher.cardCastAndDate(order.castNames.join(' · '), createdDate)
      : STRINGS.book.meineBuecher.cardDateOnly(createdDate);
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-book-border bg-book-card p-4">
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 text-left"
      >
        <p className="text-base font-semibold text-dream-navy">{displayTitle}</p>
        <p className="mt-1 text-xs text-book-text-secondary">{secondaryLine}</p>
      </button>
      {isDiscardable && (
        <button
          type="button"
          onClick={onDiscardClick}
          className="h-9 shrink-0 rounded-md border border-dream-navy/30 px-3 text-xs font-semibold text-dream-navy hover:bg-dream-navy/5"
        >
          {STRINGS.book.meineBuecher.discardButton}
        </button>
      )}
    </div>
  );
}

/**
 * Map a BookOrderStatus to a released-copy short label. Uses
 * `deriveBookSlot` (existing helper, returns a slot name from the
 * released Schrittleiste vocabulary) for the pre-payment + post-
 * payment-progress states, and existing `status.*` headings for
 * terminal states that deriveBookSlot returns null for. No new copy
 * authored — every label goes through STRINGS.
 */
function getStatusLabel(status: CustomerCollectionOrder['status']): string {
  const slot = deriveBookSlot(status, '');
  if (slot === 'personen') return STRINGS.book.schrittleiste.barA.personen;
  if (slot === 'geschichte') return STRINGS.book.schrittleiste.barA.geschichte;
  if (slot === 'figuren') return STRINGS.book.schrittleiste.barA.figuren;
  if (slot === 'vorschau') return STRINGS.book.schrittleiste.barA.vorschau;
  if (slot === 'bestellung') return STRINGS.book.schrittleiste.barA.bestellung;
  if (slot === 'freigabe') return STRINGS.book.schrittleiste.barB.freigabe;
  // slot === null — terminal / paused / cancelled / release-error /
  // image-refused. Reuse existing per-status heading copy.
  switch (status) {
    case 'cancelled':
      return STRINGS.book.status.cancelled.heading;
    case 'paused_billing':
    case 'reclamation_open':
      return STRINGS.book.status.pausedBilling.heading;
    case 'book_release_chain_error':
      return STRINGS.book.status.releaseChainError.heading;
    default:
      // Unmapped — fall through to the raw status. Not expected in
      // practice; guarded so a future enum addition never crashes.
      return status;
  }
}

